import { defineStore } from 'pinia';
import { computed, ref, toRaw } from 'vue';

import {
  createClient,
  getCities,
  getClientAccessById,
  getClientById,
  getClientFullInfoById,
  getClients,
  getClosedDeals,
  getCountries,
  getDevelopers,
  getRegions,
  updateClient,
} from 'src/logic/services/clients.service';
import type {
  CityInfo,
  ClientAccessLevel,
  ClientCreate,
  ClientInfo,
  ClientOrderByOptions,
  ClientSmallInfo,
  ClientUpdate,
  CountryInfo,
  DeveloperClassifierInfo,
  MyClosedDealsResponse,
  RegionInfo,
} from 'src/logic/types/all.types';

export const useClientStore = defineStore('client-list', () => {
  const haveToRefresh = ref<boolean>();
  const my = ref<Array<ClientInfo>>([]);
  const received = ref<Array<ClientInfo>>([]);
  const closedDeals = ref<Array<ClientInfo>>([]);
  const closedDealsData = ref<MyClosedDealsResponse>();
  const clientById = ref<ClientSmallInfo>();
  const clientByIdForMiniature = ref<ClientSmallInfo>();
  const clientByIdFullData = ref<ClientInfo | null>(null);

  const clientCreatePopupIsOpened = ref<boolean>(false);
  const clientEditPopupIsOpened = ref<boolean>(false);
  const isClientDetailsPageOpened = ref(false);
  const clientMiniaturePopupIsOpened = ref<boolean>(false);
  const selectedClient = ref<ClientInfo | null>(null);
  const selectedClientIsReceived = ref<boolean>(false);
  const selectedClientHaveChanges = ref<boolean>(false);
  const clientIdForMiniaturePopup = ref<string | null>(null);

  const selectedCountry = ref<CountryInfo | null>(null);
  const selectedRegion = ref<RegionInfo | null>(null);
  const selectedCity = ref<CityInfo | null>(null);
  const selectedDevelopers = ref<Array<DeveloperClassifierInfo>>([]);

  const countriesBE = ref<Array<CountryInfo>>([]);
  const regionsBE = ref<Array<RegionInfo>>([]);
  const citiesBE = ref<Array<CityInfo>>([]);
  const developersBE = ref<Array<DeveloperClassifierInfo>>([]);

  const searchTextCountries = ref<string>('');
  const searchTextRegions = ref<string>('');
  const searchTextCities = ref<string>('');
  const searchTextDistricts = ref<string>('');
  const searchTextDevelopers = ref<string>('');

  function mergeUniqueClients(...lists: ClientInfo[][]): ClientInfo[] {
    const clients: ClientInfo[] = [];
    const indexesById = new Map<string, number>();

    lists.flat().forEach((client) => {
      if (!client.id) {
        clients.push(client);
        return;
      }

      const existingIndex = indexesById.get(client.id);
      if (existingIndex === undefined) {
        indexesById.set(client.id, clients.length);
        clients.push(client);
      } else {
        clients[existingIndex] = client;
      }
    });

    return clients;
  }

  const countriesFiltered = computed(() => {
    return toRaw(countriesBE.value).filter((c) => {
      return c.name.toLowerCase().includes(searchTextCountries.value.toLowerCase());
    });
  });
  const regionsFiltered = computed(() => {
    return toRaw(regionsBE.value).filter((r) => {
      return (
        r.country?.id === toRaw(selectedCountry.value)?.id &&
        r.name.toLowerCase().includes(searchTextRegions.value.toLowerCase())
      );
    });
  });
  const citiesFiltered = computed(() => {
    return toRaw(citiesBE.value).filter((c) => {
      return (
        c.region?.id === toRaw(selectedRegion.value)?.id &&
        c.name.toLowerCase().includes(searchTextCities.value.toLowerCase())
      );
    });
  });

  const developersFiltered = computed(() => {
    return toRaw(developersBE.value).filter((d) => {
      return (
        d.name.toLowerCase().includes(searchTextDevelopers.value.toLowerCase()) &&
        d.country!.id === selectedCountry.value!.id
      );
    });
  });

  function fnChangeSelectedClient(client: ClientInfo | null): void {
    selectedClient.value = client;
  }

  function fnChangeClientCreatePopupState(isOpen: boolean): void {
    clientCreatePopupIsOpened.value = isOpen;
  }

  function fnChangeClientEditPopupState(isOpen: boolean): void {
    clientEditPopupIsOpened.value = isOpen;
  }

  function fnChangeClientMiniaturePopupState(isOpen: boolean): void {
    clientMiniaturePopupIsOpened.value = isOpen;
  }

  async function fnCreate(
    dto: ClientCreate,
  ): Promise<{ success: boolean; error: Record<string, string> }> {
    const { error } = await createClient(dto);

    // if they will decide to add client without refreshing to beginning of clients list
    // comment above and uncomment below (after uncommenting below you have to find code where called refresh after creation)
    // and remove logic where it clears list and gets 10 clients from scratch
    // but keep in mind (if some changes done without participation of this user [for example from admin panel] data can be not right)

    // const { client, error } = await createClient(dto);
    // if (!error && client) {
    //   my.value = [client, ...my.value];
    // }

    return { success: !error, error: error as Record<string, string> };
  }

  async function fnUpdate(clientId: string, dto: ClientUpdate): Promise<boolean> {
    const { client, error } = await updateClient(clientId, dto);

    if (!error) {
      my.value = my.value.map((cl) => {
        if (cl.id === client?.id) {
          return client!;
        }
        return cl;
      });
    }

    return !error;
  }

  async function fnDelete(client: ClientInfo): Promise<boolean> {
    const { error } = await updateClient(client.id!, {
      cash_amount: undefined,
      developers_classifier_id: null,
      districts: undefined,
      mortgage_approved: undefined,
      price: undefined,
      remarks: undefined,
      total_area: undefined,
      receiver_id: undefined,
      status: 40,
      room_count: null,
    });
    return !error;
  }

  // function internalFNSortClients(clients: Array<ClientInfo>): Array<ClientInfo> {
  //   clients.sort((a, b) => {
  //     const statusComparison = a.status - b.status;
  //     if (statusComparison !== 0) return statusComparison;
  //     return new Date(b.create_time!).getTime() - new Date(a.create_time!).getTime();
  //   });
  //   return clients;
  // }

  async function fnRefreshMy(refreshAll?: boolean): Promise<boolean> {
    const { clients, error } = await getClients('my', {
      limit: 10,
      offset: refreshAll ? 0 : my.value.length,
    });

    if (error) {
      return false;
    }

    if (clients) {
      if (refreshAll) {
        my.value = mergeUniqueClients(clients);
        return true;
      }
      my.value = mergeUniqueClients(my.value, clients);
    }

    return true;
  }

  async function fnGetClosedDeals(order_by?: ClientOrderByOptions): Promise<boolean> {
    const { deals, error } = await getClosedDeals({
      offset: closedDeals.value.length,
      limit: 40,
      order_by,
    });

    if (error) {
      return false;
    }

    if (deals) {
      closedDeals.value = [...closedDeals.value, ...deals.items];
      closedDealsData.value = deals;
    }

    return true;
  }

  async function fnGetClientById(id: string, forMiniature?: boolean): Promise<any> {
    const { client, error } = await getClientById(id);

    if (error) {
      return { error };
    }

    clientById.value = client;
    if (forMiniature) {
      clientByIdForMiniature.value = client;
    }

    return { client };
  }

  async function fnGetClientFullInfoById(id: string): Promise<any> {
    const { client, error } = await getClientFullInfoById(id);

    if (error) {
      return { error };
    }

    clientByIdFullData.value = client as ClientInfo;
    selectedClient.value = client as ClientInfo;

    return { client };
  }

  async function fnGetClientAccessLevel(id: string): Promise<ClientAccessLevel> {
    const { accessLevel, error } = await getClientAccessById(id);

    if (error) {
      return 0;
    }

    return accessLevel!;
  }

  function fnResetClientData(): void {
    clientByIdFullData.value = null;
    clientById.value = undefined;
  }

  async function fnRefreshReceived(refreshAll?: boolean): Promise<boolean> {
    const { clients, error } = await getClients('received', {
      limit: 10,
      offset: refreshAll ? 0 : received.value.length,
    });

    if (error) {
      return false;
    }

    // received.value = internalFNSortClients(clients ? clients : []).filter((c) => c.status !== 40 && c.status !== 0,) || [];

    if (clients) {
      if (refreshAll) {
        received.value = mergeUniqueClients(clients);
        return true;
      }
      received.value = mergeUniqueClients(received.value, clients);
    }

    return true;
  }

  async function fnRefreshCountries(): Promise<boolean> {
    const { countries, error } = await getCountries({});

    if (error) {
      return false;
    }

    countriesBE.value = countries || [];

    return true;
  }

  async function fnRefreshRegions(): Promise<boolean> {
    const { regions, error } = await getRegions({});

    if (error) {
      return false;
    }

    regionsBE.value = regions || [];

    return true;
  }

  async function fnRefreshCities(): Promise<boolean> {
    const { cities, error } = await getCities({});

    if (error) {
      return false;
    }

    citiesBE.value = cities || [];

    return true;
  }

  async function fnRefreshDevelopers(country_id?: number): Promise<boolean> {
    const { developers, error } = await getDevelopers({ country_id });

    if (error) {
      return false;
    }

    developersBE.value = developers?.filter((d) => d.not_displayed === false) || [];

    return true;
  }

  function fnSelectCountry(country: CountryInfo | null): void {
    searchTextCountries.value = '';
    selectedCountry.value = country;
    fnSelectRegion(null);
  }

  function fnSelectRegion(region: RegionInfo | null): void {
    searchTextRegions.value = '';
    selectedRegion.value = region;
    fnSelectCity(null);
  }

  function fnSelectCity(city: CityInfo | null): void {
    searchTextCities.value = '';
    selectedCity.value = city;
  }

  function fnResetDevelopers(): void {
    selectedDevelopers.value = [];
  }

  function fnToggleDeveloper(developer: DeveloperClassifierInfo): void {
    const exists = selectedDevelopers.value.some((d) => d.id === developer.id);
    if (exists) {
      selectedDevelopers.value = selectedDevelopers.value.filter((d) => d.id !== developer.id);
    } else {
      selectedDevelopers.value = [...selectedDevelopers.value, developer];
    }
  }

  function fnResetClientStore(): void {
    haveToRefresh.value = false;
    my.value = [];
    received.value = [];
    clientEditPopupIsOpened.value = false;
    clientMiniaturePopupIsOpened.value = false;
    selectedClient.value = null;
    selectedCountry.value = null;
    selectedRegion.value = null;
    selectedCity.value = null;
    selectedDevelopers.value = [];
    countriesBE.value = [];
    regionsBE.value = [];
    citiesBE.value = [];
    developersBE.value = [];
    searchTextCountries.value = '';
    searchTextRegions.value = '';
    searchTextCities.value = '';
    searchTextDistricts.value = '';
    searchTextDevelopers.value = '';
  }

  return {
    haveToRefresh,
    my,
    received,
    closedDeals,
    closedDealsData,
    clientCreatePopupIsOpened,
    clientEditPopupIsOpened,
    clientById,
    clientByIdFullData,
    clientMiniaturePopupIsOpened,
    isClientDetailsPageOpened,
    selectedClient,
    selectedClientHaveChanges,
    selectedClientIsReceived,
    selectedCountry,
    selectedRegion,
    selectedCity,
    selectedDevelopers,
    countriesBE,
    regionsBE,
    citiesBE,
    developersBE,
    searchTextCountries,
    searchTextRegions,
    searchTextCities,
    searchTextDistricts,
    searchTextDevelopers,
    countriesFiltered,
    regionsFiltered,
    citiesFiltered,
    developersFiltered,
    clientIdForMiniaturePopup,
    clientByIdForMiniature,
    fnGetClientAccessLevel,
    fnResetClientData,
    fnChangeClientCreatePopupState,
    fnChangeClientEditPopupState,
    fnChangeClientMiniaturePopupState,
    fnChangeSelectedClient,
    fnGetClientFullInfoById,
    fnCreate,
    fnUpdate,
    fnGetClosedDeals,
    fnGetClientById,
    fnDelete,
    fnRefreshMy,
    fnRefreshReceived,
    fnRefreshCountries,
    fnRefreshRegions,
    fnRefreshCities,
    fnRefreshDevelopers,
    fnSelectCountry,
    fnSelectRegion,
    fnSelectCity,
    fnResetDevelopers,
    fnToggleDeveloper,
    fnResetClientStore,
  };
});
